/**
 * /work-orders/new
 */
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ApiError, createWorkOrder, listVehicles, listVendors } from '@/lib/api'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Field } from '@/components/ui/field'

export function WorkOrderNewPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const vehiclesQ = useQuery({ queryKey: ['vehicles'], queryFn: () => listVehicles() })
  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => listVendors() })

  const [vehicleId, setVehicleId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('Normal')
  const [vendorId, setVendorId] = useState('')
  const [lineDesc, setLineDesc] = useState('')
  const [lineQty, setLineQty] = useState('1')
  const [lineCost, setLineCost] = useState('0')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: createWorkOrder,
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
      void navigate({
        to: '/work-orders/$workOrderId',
        params: { workOrderId: res.workOrder.id },
      })
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Error al crear')
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!vehicleId || !title) {
      setError('Vehículo y título son obligatorios')
      return
    }
    mutation.mutate({
      vehicleId,
      title,
      description: description || null,
      priority,
      vendorId: vendorId || null,
      items: lineDesc
        ? [
            {
              itemType: 'Part',
              description: lineDesc,
              quantity: Number(lineQty) || 1,
              unitCost: Number(lineCost) || 0,
            },
          ]
        : [],
    })
  }

  return (
    <>
      <PageHeader
        title="New work order"
        actions={
          <Link to="/work-orders" className="text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </Link>
        }
      />
      <Card className="mx-auto max-w-xl">
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-3">
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">
                Vehículo <span className="text-danger">*</span>
              </span>
              <select
                required
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                <option value="">Seleccionar…</option>
                {(vehiclesQ.data?.vehicles ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.unitNumber}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label="Título"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Field
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">Prioridad</span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {['Low', 'Normal', 'High', 'Critical'].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">Proveedor</span>
              <select
                className="h-11 rounded-lg border border-input bg-card px-3"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
              >
                <option value="">—</option>
                {(vendorsQ.data?.vendors ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.companyName}
                  </option>
                ))}
              </select>
            </label>
            <div className="rounded-xl border border-border p-3">
              <p className="mb-2 text-sm font-medium">Primera línea (opcional)</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <Field
                  label="Descripción"
                  value={lineDesc}
                  onChange={(e) => setLineDesc(e.target.value)}
                  containerClassName="sm:col-span-3"
                />
                <Field
                  label="Qty"
                  type="number"
                  value={lineQty}
                  onChange={(e) => setLineQty(e.target.value)}
                />
                <Field
                  label="Unit cost"
                  type="number"
                  value={lineCost}
                  onChange={(e) => setLineCost(e.target.value)}
                />
              </div>
            </div>
            <Button type="submit" className="w-full" loading={mutation.isPending}>
              Crear orden
            </Button>
          </form>
        </CardBody>
      </Card>
    </>
  )
}
